import { Action } from './action';

type AuthorizationRecord = { id: string; action: Action; reversedAuthorizationId: string | null; created: Date };

/** Every authorization that hasn't been reversed, oldest first: the first one and its supplementals. */
export function getActiveAuthorizations<T extends AuthorizationRecord>(records: T[]): T[] {
  const reversed = new Set(records.map(record => record.reversedAuthorizationId).filter(Boolean));

  return records
    .filter(record => record.action === Action.AUTHORIZED && !reversed.has(record.id))
    .sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime());
}

/** The latest authorization that hasn't been reversed, or null. */
export function getActive<T extends AuthorizationRecord>(records: T[]): T | null {
  const active = getActiveAuthorizations(records);
  return active[active.length - 1] ?? null;
}

const FIGURES = ['requestedTotal', 'authorizedGross', 'tax', 'deductible', 'netPayable'] as const;
type Figures = Record<(typeof FIGURES)[number], number>;

/** The claim's totals minus the active authorizations: what a new authorization adds. The first one adds everything. */
export function getOwnTotals<T extends Figures>(totals: T, active: Figures[]): T {
  const own = { ...totals };
  for (const key of FIGURES)
    own[key] = (totals[key] - active.reduce((sum, record) => sum + record[key], 0)) as T[typeof key];
  return own;
}
